import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Produto from "model/administracao/cadastro/produto/Produto";
import { FilterMatchMode } from "primereact/api";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { ColumnFilterApplyTemplateOptions, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTableFilterMeta, DataTableOperatorFilterMetaData } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useEffect, useState } from "react";
import { produtoCompleteMethod, produtoItemTemplate } from "./autocompleteUtils";

export const TextMatchModeOptions = [
    { label: 'Contém', value: FilterMatchMode.CONTAINS },
    { label: 'Igual', value: FilterMatchMode.EQUALS },
    { label: 'Começa com', value: FilterMatchMode.STARTS_WITH },
    { label: 'Termina com', value: FilterMatchMode.ENDS_WITH },
]

export const NumberMatchModeOptions = [
    { label: 'Igual', value: FilterMatchMode.EQUALS },
    { label: 'Maior que', value: FilterMatchMode.GREATER_THAN },
    { label: 'Menor que', value: FilterMatchMode.LESS_THAN },
    { label: 'Maior ou igual', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
    { label: 'Menor ou igual', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
]

export const DateMatchModeOptions = [
    { label: 'Igual', value: FilterMatchMode.EQUALS },
    { label: 'Maior que', value: FilterMatchMode.GREATER_THAN },
    { label: 'Menor que', value: FilterMatchMode.LESS_THAN },
    { label: 'Maior ou igual', value: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
    { label: 'Menor ou igual', value: FilterMatchMode.LESS_THAN_OR_EQUAL_TO },
]

export const ListMatchModeOptions = [
    { label: 'Está em', value: FilterMatchMode.IN },
    { label: 'Não está em', value: FilterMatchMode.NOT_EQUALS }
]

export const IdMatchModeOptions = [
    { label: 'Igual', value: FilterMatchMode.EQUALS },
    { label: 'Diferente', value: FilterMatchMode.NOT_EQUALS }
]

export const IdListMatchModeOptions = [
    { label: 'Está em', value: FilterMatchMode.IN },
    { label: 'Não está em', value: FilterMatchMode.NOT_EQUALS }
]

export const numberFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <InputNumber
        value={options.value} onValueChange={(e) => options.filterCallback(e.value)}
        useGrouping={false} minFractionDigits={0} maxFractionDigits={0}
        placeholder="Escreva o filtro"
    />;
}
export const decimalFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <InputNumber
        value={options.value} onValueChange={(e) => options.filterCallback(e.value)}
        useGrouping={false} minFractionDigits={1} maxFractionDigits={20}
        placeholder="Escreva o filtro" locale="pt-BR"
    />;
}

export const dropdownFilterTemplate = (options: ColumnFilterElementTemplateOptions, itens: Array<any>, optionLabel = "nome", optionValue = "id") => {
    return <Dropdown value={options.value} options={itens} filter
        onChange={(e) => options.filterCallback(e.value)}
        optionLabel={optionLabel} optionValue={optionValue} placeholder="Qualquer" className="p-column-filter" />
}

export const multiselectFilterTemplate = (options: ColumnFilterElementTemplateOptions, itens: Array<any>, optionLabel = "nome", optionValue = "id") => {
    return <MultiSelect value={options.value} options={itens} filter
        onChange={(e) => options.filterCallback(e.value)}
        optionLabel={optionLabel} optionValue={optionValue} placeholder="Qualquer" className="p-column-filter" />
}

export const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions, opcoes: any[]) => {
    return <MultiSelect value={options.value} options={opcoes}
        onChange={(e: MultiSelectChangeEvent) => options.filterCallback(e.value)}
        optionLabel="label" optionValue="value" placeholder="Qualquer" className="p-column-filter"
    />;
};

export const booleanFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <Dropdown value={options.value} options={[
        { label: 'Sim', value: true },
        { label: 'Não', value: false }
    ]}
        onChange={(e: DropdownChangeEvent) => {
            options.filterCallback(e.value);
        }}
        optionLabel="label" optionValue="value" placeholder="Qualquer" className="p-column-filter"
    />;
};

export const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <DatePicker
        slotProps={{
            popper: {
                disablePortal: true,
            },
        }}
        value={dayjs(options.value)}
        onChange={value =>
            options.filterCallback(value?.toDate(), options.index)
        }
    />
};

export const dateRangeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return <div style={{ display: "flex", alignItems: "center" }}>
        <DatePicker
            slotProps={{
                popper: {
                    disablePortal: true,
                },
            }}
            value={options.value?.inicio ? dayjs(options.value.inicio) : null}
            onChange={value => {
                if (value?.toDate())
                    options.filterCallback({ ...options.value, inicio: value?.toDate() }, options.index)
            }}
        />
        <label style={{ marginLeft: 8, marginRight: 8 }}>à</label>

        <DatePicker
            slotProps={{
                popper: {
                    disablePortal: true,
                },
            }}
            value={options.value?.fim ? dayjs(options.value.fim) : null}
            onChange={value => {
                if (value?.toDate())
                    options.filterCallback({ ...options.value, fim: value?.toDate() }, options.index)
            }}
        />
    </div >
};

interface ProdutoFilterTemplateProps {
    options: ColumnFilterElementTemplateOptions;
    opcoes: Produto[];
}

export const ProdutoFilterTemplate: React.FC<ProdutoFilterTemplateProps> = ({ options, opcoes }) => {

    const [filtered, setFiltered] = useState<Produto[]>([]);
    const [produtoSelecionado, setProdutoSelecionado] = useState<Produto>(new Produto());

    useEffect(() => {
        let prod = opcoes.find(p => p.codigo == options.value);
        if (prod) setProdutoSelecionado(prod);
    }, [])

    return <AutoComplete
        dropdown
        placeholder="Selecione o produto" value={produtoSelecionado}
        suggestions={filtered} field="nome"
        itemTemplate={(item: Produto) => produtoItemTemplate(item)}
        completeMethod={(e) => {
            let list = produtoCompleteMethod(opcoes, e.query)
            setFiltered(list);
        }}
        onChange={(e) => {
            if (typeof e.target.value == "string") {
                let prod = new Produto();
                prod.nome = e.target.value;
                options.filterCallback(e.target.value);
                setProdutoSelecionado(prod);
            } else {
                options.filterCallback(e.target.value.codigo);
                setProdutoSelecionado(e.target.value);
            }
        }}
        inputStyle={{ width: '100%' }}
        style={{ width: '100%' }}
    />
};


export const filterApplyTemplate = (options: ColumnFilterApplyTemplateOptions) => {
    return <Button type="button" label="Aplicar" onClick={options.filterApplyCallback} ></Button>;
};

export function getFilters(filter: DataTableFilterMeta | null | undefined): string {

    let filters = '';
    if (filter) {
        let filterCount = 0;
        filters += '?filters=';
        Object.keys(filter).forEach((key, index) => {
            if (key === 'null') return;

            let filterField = filter[key] as DataTableOperatorFilterMetaData;
            let value = filterField.constraints[0].value;

            if (value == null) return;


            filters += encodeURIComponent(filterCount > 0 ? ',' : '{');
            filterCount++;

            if (typeof value == "object") {
                if (value instanceof Date) {
                    filters += encodeURIComponent(`"${key}":{"value":${JSON.stringify(value) || ''}`);
                } else if (value instanceof Array) {
                    filters += encodeURIComponent(`"${key}":{"values":${JSON.stringify(value) || '[]'}`);
                } else {
                    filters += encodeURIComponent(`"${key}":{${Object.keys(value).map((valueKey) => `"${valueKey}":${JSON.stringify(value[valueKey])}`).join(',')}`);
                }

            } else if (typeof value == "number") {
                filters += encodeURIComponent(`"${key}":{"value":${value || 0}`);
            } else if (typeof value == "boolean") {
                filters += encodeURIComponent(`"${key}":{"value":"${value}"`);
            } else {
                filters += encodeURIComponent(`"${key}":{"value":"${value || ''}"`);
            }

            filters += encodeURIComponent(`,"type":"${filterField.constraints[0].matchMode?.toUpperCase()}"}`);

        });
        if (filters.length > 9) {
            filters += encodeURIComponent('}');
        }
    }

    return filters;
}